import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, MapPin, Ruler } from "lucide-react";
import { Button } from "@/components/ui/button";

const badges = [
  { icon: ShieldCheck, text: "100% Free Estimate" },
  { icon: MapPin, text: "City-Accurate Rates 2026" },
  { icon: Ruler, text: "IS 456 / CPWD Based" },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1722421492323-eaf9c401befe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBpbmRpYW4lMjBob3VzZSUyMGFyY2hpdGVjdHVyZSUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3OTAyNjQ2NDJ8MA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  return (
    <section className="relative overflow-hidden border-b border-slate-800" data-testid="hero">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Modern Indian house" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/40" />
        <div className="absolute inset-0 grain-overlay opacity-[0.15]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/10 px-3 py-1 text-xs font-semibold text-orange-400 mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-orange-500 animate-pulse" />
            Advanced Construction Cost Calculator · India
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
            Build your dream home with{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
              zero cost surprises
            </span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Estimate total construction cost, cement · steel · sand · bricks quantities,
            compare across Indian cities & quality tiers, and plan your loan EMI — all in one
            precise calculator.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              onClick={() => scrollTo("calculator")}
              data-testid="hero-cta-calculate"
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl h-12 px-6 gap-2 shadow-lg shadow-orange-500/20"
            >
              Start Estimating <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => scrollTo("comparison")}
              variant="outline"
              data-testid="hero-cta-compare"
              className="rounded-xl h-12 px-6 border-slate-700 bg-slate-900/50 text-white hover:bg-slate-800 hover:text-white"
            >
              Compare Cities
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
            {badges.map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                <b.icon className="h-4 w-4 text-emerald-400" />
                {b.text}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
